import React from "react";
import { useTranslation } from "react-i18next";
import { CalendarItem } from "../../features/calendar/models/CalendarItem";
import { CalendarView } from "../../features/calendar/models/CalendarView";
import { ClosedDay } from "../../features/closed-days/models/ClosedDay";
import { Instructor } from "../../features/instructors/models/Instructor";
import CalendarToolbar from "./CalendarToolbar";
import CalendarSummary from "./CalendarSummary";

interface CalendarPageHeaderProps {
  rangeLabel: string;
  view: CalendarView;
  items: CalendarItem[];
  closedByDate: Map<string, ClosedDay>;
  /** Suppressed while the grid is a skeleton — counts of nothing say nothing. */
  showSummary: boolean;
  hasError: boolean;
  instructors: Instructor[];
  instructorFilter: string | null;
  onViewChange: (view: CalendarView) => void;
  onPrevious: () => void;
  onToday: () => void;
  onNext: () => void;
  onInstructorFilterChange: (instructorId: string | null) => void;
}

/** Title, range navigation, the at-a-glance summary and the load-error banner. */
const CalendarPageHeader: React.FC<CalendarPageHeaderProps> = ({
  rangeLabel,
  view,
  items,
  closedByDate,
  showSummary,
  hasError,
  instructors,
  instructorFilter,
  onViewChange,
  onPrevious,
  onToday,
  onNext,
  onInstructorFilterChange,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <h1>{t("calendar.title")}</h1>
      <CalendarToolbar
        rangeLabel={rangeLabel}
        view={view}
        onViewChange={onViewChange}
        onPrevious={onPrevious}
        onToday={onToday}
        onNext={onNext}
        instructors={instructors}
        instructorFilter={instructorFilter}
        onInstructorFilterChange={onInstructorFilterChange}
      />
      {showSummary && <CalendarSummary items={items} closedByDate={closedByDate} />}
      {hasError && (
        <div className="mt-4 rounded-lg bg-red-100 p-3 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300">
          {t("calendar.loadFailed")}
        </div>
      )}
    </>
  );
};

export default CalendarPageHeader;
